import { RecurringTypesEnum } from "dash-common";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import {
  SUBSCRIPTION_COLORS,
  ENABLE_YEARLY_PLANS,
  SUBSCRIPTION_CONFIG,
} from "../SubscriptionComponents.constants";

interface IRecurringToggleV3 {
  recurringType?: RecurringTypesEnum;
  onToggle: (newRecurringType: RecurringTypesEnum) => void;
  className?: string;
  activeColor?: string;
  inactiveColor?: string;
}

const RecurringToggleV3 = ({
  onToggle,
  recurringType,
  className = "",
  activeColor,
  inactiveColor,
}: IRecurringToggleV3) => {
  const theme = useTheme();
  const { t } = useTranslation("pages");

  // Determine extended period type and labels based on feature flag
  const extendedPeriodType = ENABLE_YEARLY_PLANS
    ? RecurringTypesEnum.YEAR
    : RecurringTypesEnum.SIXMONTH;

  const config = ENABLE_YEARLY_PLANS
    ? SUBSCRIPTION_CONFIG.yearly
    : SUBSCRIPTION_CONFIG.sixMonth;

  const extendedPeriodLabel = ENABLE_YEARLY_PLANS
    ? t("subscriptionPlans.yearly")
    : t("subscriptionPlans.semiAnnually");
  const extendedPeriodText = ENABLE_YEARLY_PLANS
    ? t("subscriptionPlans.withYearly")
    : t("subscriptionPlans.withSixMonths");

  const handleToggle = () => {
    const newRecurringType =
      recurringType === RecurringTypesEnum.MONTH
        ? extendedPeriodType
        : RecurringTypesEnum.MONTH;

    onToggle(newRecurringType);
  };

  // Treat SIXMONTH same as extended period for toggle display
  // SIXMONTH plans are deprecated - visually group them with extended periods (YEAR)
  // since they're closer to yearly than monthly. handleToggle() will correctly
  // switch them to MONTH when toggled.
  const isExtendedPeriod =
    recurringType === extendedPeriodType ||
    recurringType === RecurringTypesEnum.SIXMONTH;

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      {/* Dynamic discount text above */}
      <div className="text-sm font-medium">
        <span style={{ color: activeColor || SUBSCRIPTION_COLORS.primary }}>
          {t("subscriptionPlans.saveDiscount", { percent: config.discount })}
        </span>
        <span style={{ color: SUBSCRIPTION_COLORS.secondaryText }}>
          {" "}
          {extendedPeriodText}
        </span>
      </div>

      {/* Toggle with sliding pill */}
      <div
        className="relative h-12 flex items-center rounded-xl p-1.5 cursor-pointer border"
        style={{
          width: "300px",
          backgroundColor: inactiveColor || theme.colors.white,
          borderColor: activeColor || SUBSCRIPTION_COLORS.primary,
        }}
        onClick={handleToggle}
      >
        {/* Sliding background pill */}
        <div
          className={`absolute top-1.5 bottom-1.5 left-1.5 w-[calc(50%-6px)] rounded-md transition-transform duration-300 ${
            !isExtendedPeriod
              ? "transform translate-x-0"
              : "transform translate-x-full"
          }`}
          style={{
            backgroundColor: activeColor || SUBSCRIPTION_COLORS.primary,
          }}
        ></div>

        <span
          className={`flex-1 text-center z-10 transition-colors duration-300 font-medium ${
            !isExtendedPeriod ? "text-white" : "text-gray-400"
          }`}
        >
          {t("subscriptionPlans.monthly")}
        </span>

        <span
          className={`flex-1 text-center z-10 transition-colors duration-300 font-medium ${
            isExtendedPeriod ? "text-white" : "text-gray-400"
          }`}
        >
          {extendedPeriodLabel}
        </span>
      </div>
    </div>
  );
};

export { RecurringToggleV3 };
